import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import type {
  AttachmentPermission,
  ProjectAttachmentAccess,
  ProjectAttachmentAccessCreateData,
  ProjectAttachmentShareLink,
  ProjectAttachmentShareLinkCreateData,
  ShareLinkTarget
} from "../types/project-attachment-sharing.types.ts"

const ACCESS_BASE_URL = "/project-management/project-attachment-access"
export const SHARE_LINK_BASE_URL = "/project-management/project-attachment-share-links"

const PATHS = {
  access: `${ACCESS_BASE_URL}/`,
  accessDetail: (id: string) => `${ACCESS_BASE_URL}/${id}/`,
  changePermission: (id: string) => `${ACCESS_BASE_URL}/${id}/change_permission/`,
  shareLinks: `${SHARE_LINK_BASE_URL}/`,
  disableShareLink: (id: string) => `${SHARE_LINK_BASE_URL}/${id}/disable/`
}

// GET /project-management/project-attachment-access/?attachment=:id — who
// already has explicit access, and at what level.
export const getAttachmentAccess = async (attachmentId: string) => {
  return request.api.get<
    BasePagination<ProjectAttachmentAccess>,
    BaseApiResponse<unknown>
  >(PATHS.access, { attachment: attachmentId })
}

// POST /project-management/project-attachment-access/ — grants access, or
// upserts the permission level if the target already has a grant.
export const createAttachmentAccess = async (
  data: ProjectAttachmentAccessCreateData
) => {
  return request.api.post<
    BaseApiResponse<ProjectAttachmentAccess>,
    BaseApiResponse<unknown>
  >(PATHS.access, data)
}

// DELETE /project-management/project-attachment-access/:id/ — revoke.
export const removeAttachmentAccess = async (id: string) => {
  return request.api.delete<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.accessDetail(id)
  )
}

// POST /project-management/project-attachment-access/:id/change_permission/
export const changeAttachmentAccessPermission = async (
  id: string,
  permission: AttachmentPermission
) => {
  return request.api.post<
    BaseApiResponse<ProjectAttachmentAccess>,
    BaseApiResponse<unknown>
  >(PATHS.changePermission(id), { permission })
}

// GET /project-management/project-attachment-share-links/?attachment=:id
// or ?album_attachment=:id
export const getAttachmentShareLinks = async (target: ShareLinkTarget) => {
  return request.api.get<
    BasePagination<ProjectAttachmentShareLink>,
    BaseApiResponse<unknown>
  >(PATHS.shareLinks, target)
}

// POST /project-management/project-attachment-share-links/
export const createAttachmentShareLink = async (
  data: ProjectAttachmentShareLinkCreateData
) => {
  return request.api.post<
    BaseApiResponse<ProjectAttachmentShareLink>,
    BaseApiResponse<unknown>
  >(PATHS.shareLinks, data)
}

// POST /project-management/project-attachment-share-links/:id/disable/
export const disableAttachmentShareLink = async (id: string) => {
  return request.api.post<
    BaseApiResponse<ProjectAttachmentShareLink>,
    BaseApiResponse<unknown>
  >(PATHS.disableShareLink(id))
}
